<template>
  <Teleport to="#modal">
    <transition name="modal" appear>
      <div v-if="isOpen" ref="modal" :class="classes" class="modal">
        <div class="modal__backdrop" @click="close">
          <AppContainer :size="size">
            <div class="modal__content" @click.stop>
              <div class="modal__close">
                <button type="button" @click="close">
                  <CloseIcon />
                </button>
              </div>
              <slot />
            </div>
          </AppContainer>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import AppContainer from '@/components/shared/AppContainer';
import CloseIcon from '@/components/icons/CloseIcon';


export default {
  components: {
    AppContainer,
    CloseIcon
  },
  props: {
    close: {
      type: Function,
      required: true
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'xl',
      validator: value => ['sm', 'md', 'lg', 'xl'].includes(value)
    },
    spacing: {
      type: Boolean,
      required: false,
      default: true
    },
    theme: {
      type: String,
      required: false,
      default: 'white',
      validator: value => ['white', 'black'].includes(value)
    }
  },
  computed: {
    classes() {
      const classes = [`modal--${this.theme}`];

      if (this.spacing) {
        classes.push('modal--spacing');
      }

      return classes;
    }
  },
  watch: {
    isOpen(isOpen) {
      if (isOpen) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    }
  },
  beforeUnmount() {
    this.handleClose();
  },
  mounted() {
    if (this.isOpen) {
      this.handleOpen();
    }
  },
  methods: {
    handleClose() {
      enableBodyScroll(this.$refs.modal);
    },
    handleOpen() {
      // needed because the element isn't in the dom yet :(
      // https://github.com/LinusBorg/portal-vue/issues/118
      this.$nextTick(() => {
        this.$nextTick(() => {
          disableBodyScroll(this.$refs.modal);
        });
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.modal {
  background-color: rgba(map-get($colors, 'black'), 0.4);
  height: 100%;
  left: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 7;

  &__backdrop {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 100%;
    padding: spacing('xxl') 0;
  }

  &__content {
    position: relative;

    .modal--spacing & {
      padding: spacing('lg') spacing('md');

      @include breakpoint('md') {
        padding: spacing('xl', 1.5) spacing('xl', 2);
      }
    }

    .modal--white & {
      background-color: color('white');
    }

    .modal--black & {
      background-color: color('black');
      color: color('white');
    }
  }

  &__close {
    position: absolute;
    right: spacing('lg');
    top: spacing('lg');
    z-index: 1;

    .modal--white & {
      color: color('primary');
    }
    .modal--black & {
      color: color('white');
    }
  }

  // transitions
  &-enter-active,
  &-leave-active {
    transition: opacity 0.25s;
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }
}
</style>
